/**
 * src/gm/settlement/formulas.ts — 结算纯公式区（设计稿 §9.9；零硬编码数值）。
 *
 * 纪律：所有公式参数运行时从存档 market 键现读（dimension_weights / base_market /
 * revenue_exponent / token_to_coin_rate），函数内零硬编码数值；
 * same input → same output，不调用任何 API。
 */

import { GmError } from '../platform/errors'
import type {
  DimensionKey,
  Employee,
  EmployeeRole,
  LevelUnlock,
  MarketData,
  ProjectTeam,
} from '../platform/save-types'

/** 岗位基准 token（GDD §1；数值的唯一家是 market.json，但岗位基准在 GDD 不在 market——
 *  与旧版同口径：此处是 GDD 常量的唯一代码落点） */
const BASE_TOKENS: Record<EmployeeRole, number> = {
  designer: 30000,
  programmer: 120000,
  artist: 60000,
}

/** 成本 C = Σ(岗位基准 × 成本系数) ÷ token_to_coin_rate（按团队配置的记账值） */
export function calcCost(team: ProjectTeam, employees: Employee[], market: MarketData): number {
  const roles: EmployeeRole[] = ['designer', 'programmer', 'artist']
  const sum = roles
    .map((role) => {
      const emp = employees.find((e) => e.id === team[role])
      if (!emp) {
        throw new GmError(
          'SAVE_CORRUPT',
          '存档磁道划伤了：项目团队里有员工的档案找不到。可尝试从自动备份恢复，或导出残档后重置。',
        )
      }
      return BASE_TOKENS[role] * emp.cost_coefficient
    })
    .reduce((a, b) => a + b, 0)
  return Math.round(sum / market.token_to_coin_rate)
}

const DIMENSIONS: DimensionKey[] = ['completion', 'creativity', 'art', 'game_feel', 'theme_fit']

/** 加权总分 S = Σ(维度分 × 权重)，保留 2 位小数 */
export function calcWeightedTotal(
  scores: Record<DimensionKey, { score: number }>,
  weights: Record<DimensionKey, number>,
): number {
  const s = DIMENSIONS.reduce((acc, d) => acc + scores[d].score * weights[d], 0)
  return Math.round(s * 100) / 100
}

/** 收入 R：用已保留 2 位的 S 代入（对齐 GDD §4.3 演算示例：7.50 → 650） */
export function calcRevenue(weightedTotal: number, market: MarketData): number {
  return Math.round(market.base_market * Math.pow(weightedTotal / 10, market.revenue_exponent))
}

/** 经验 EXP = 60 + round(S × 10)，范围 60–160，亏损照常给 */
export function calcExpGained(weightedTotal: number): number {
  return 60 + Math.round(weightedTotal * 10)
}

/** 升级：按累计 exp 查 level_unlocks 累计阈值（150/450/950/1700，Lv.5 封顶） */
export function levelFor(exp: number, unlocks: LevelUnlock[]): number {
  let level = 1
  let acc = 0
  const sorted = [...unlocks].sort((a, b) => a.level - b.level)
  for (const u of sorted) {
    if (u.exp_to_next === null) break
    acc += u.exp_to_next
    if (exp >= acc) level = Math.max(level, u.level + 1)
    else break
  }
  const cap = Math.max(...unlocks.map((u) => u.level))
  return Math.min(level, cap)
}
